/**
 * The door, laid out the way utucat's is: the pitch on a teal hero to the
 * left, the card on the right; on a phone, the lockup above the card and the
 * rest below it, so the form is on the first screen.
 *
 * The card has three faces: the ways in, "check your inbox" once a link is on
 * its way, and -- arriving from that link -- one button that spends it. The
 * button is not a formality: corporate mail scanners open every link before
 * the person does, so opening one must claim nothing (docs/notes/auth.md).
 *
 * Google and Microsoft are links, not fetches: each is a navigation to the
 * provider and back, which ends signed in on `/` or here with a refusal. A
 * button is only shown for a provider the worker says is set up.
 *
 * While self-serve signup is open (the worker hands over Turnstile's site
 * key), the same door is the way in for somebody new: the widget sits in the
 * email form, since a link to an address with no account is what it guards,
 * and arriving from the website's /signup changes only the words. While
 * utuwrk is invite-only, an address that is not on the list is told so,
 * plainly; see docs/notes/auth.md for why that is the right trade.
 */

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { ROUTES, validEmail } from "@utuwrk/core";
import { ApiError, api } from "../lib/api.js";
import { Turnstile, type TurnstileHandle } from "../lib/turnstile.js";
import { Icon, type IconName } from "./Icon.js";

const sentence = (problem: unknown): string =>
  problem instanceof ApiError ? problem.message : "Something went wrong. Try again.";

export function SignIn({
  token,
  refusal,
  signingUp = false,
  onSignedIn,
  onLinkSpent,
}: {
  /** From a mailed link: the card asks for one press instead of an address. */
  token: string | null;
  /** Where a Google or Microsoft sign-in was refused, the sentence why. */
  refusal: string | null;
  /** Arrived at /signup: the same door, worded for somebody new. */
  signingUp?: boolean;
  onSignedIn: () => Promise<void>;
  /** The token is spent or refused: take it out of the address bar. */
  onLinkSpent: () => void;
}) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(refusal);
  const [providers, setProviders] = useState({ google: false, microsoft: false });
  /** Turnstile's site key while signup is open; null while utuwrk is invite-only. */
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const [passToken, setPassToken] = useState<string | null>(null);
  const check = useRef<TurnstileHandle>(null);

  useEffect(() => {
    let live = true;
    api
      .health()
      .then((report) => {
        if (!live) return;
        setProviders({ google: report.google, microsoft: report.microsoft });
        setSiteKey(report.signup ?? null);
      })
      // Without an answer the email link is still there, which is the door that always works.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  async function confirm() {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await api.confirmSignIn(token);
      onLinkSpent();
      await onSignedIn();
    } catch (problem) {
      setError(sentence(problem));
      onLinkSpent();
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const address = email.trim();
    if (!validEmail(address)) {
      setError("That does not look like an email address.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.startSignIn(address, passToken ?? undefined);
      setSentTo(address);
    } catch (problem) {
      setError(sentence(problem));
    } finally {
      setBusy(false);
      // Spent or refused, a token is no use again.
      check.current?.reset();
    }
  }

  const anyProvider = providers.google || providers.microsoft;

  return (
    <div className="door">
      <section className="door-brand">
        <div className="auth-lockup">
          <img src="/icon.svg" alt="" width={44} height={44} />
          <span className="wordmark wordmark-large" aria-label="utuwrk">
            utu<span className="wordmark-soft">wrk</span>
          </span>
        </div>
        <h1 className="door-headline">
          Ready on day one.
          <br />
          Closed on the last.
        </h1>
      </section>

      <section className="door-story" aria-label="What utuwrk does">
        <HandoffDemo />
        <ul className="door-features">
          <Feature icon="clipboard" title="Every task a card">
            One owning department, and the cards it waits on.
          </Feature>
          <Feature icon="bell" title="The next owner told">
            The moment their turn comes, and again when it is late.
          </Feature>
          <Feature icon="shield" title="An audit nobody can edit">
            Who did what, and when, for good.
          </Feature>
        </ul>
      </section>

      <main className="door-main">
        <section className="door-card" aria-labelledby="auth-title">
          {token ? (
            <div className="door-sent">
              <span className="door-sent-icon">
                <Icon name="mail" size={26} />
              </span>
              <h2 id="auth-title" className="auth-title">
                Finish signing in
              </h2>
              <p className="muted">Press the button to sign in on this device.</p>
              {error ? <Problem>{error}</Problem> : null}
              <button
                className="button button-block"
                type="button"
                disabled={busy}
                onClick={() => void confirm()}
              >
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </div>
          ) : sentTo ? (
            <div className="door-sent">
              <span className="door-sent-icon">
                <Icon name="mail" size={26} />
              </span>
              <h2 id="auth-title" className="auth-title">
                Check your inbox
              </h2>
              <p className="muted">
                A sign-in link is on its way to <strong className="auth-address">{sentTo}</strong>. It works
                once, for 15 minutes.
              </p>
              <button
                className="button button-quiet button-block"
                type="button"
                onClick={() => {
                  setSentTo(null);
                  setError(null);
                }}
              >
                Use another address
              </button>
            </div>
          ) : (
            <>
              <div className="auth-head">
                <h2 id="auth-title" className="auth-title">
                  {siteKey && signingUp ? "Start free" : "Sign in"}
                </h2>
                {siteKey ? null : <span className="pill s-waiting">Invite only</span>}
              </div>

              {error ? <Problem>{error}</Problem> : null}

              {anyProvider ? (
                <>
                  <div className="door-providers">
                    {providers.google ? (
                      <a className="button button-quiet button-block" href={ROUTES.googleStart}>
                        <img src="/brand/google.svg" alt="" width={18} height={18} />
                        Continue with Google
                      </a>
                    ) : null}
                    {providers.microsoft ? (
                      <a className="button button-quiet button-block" href={ROUTES.microsoftStart}>
                        <img src="/brand/microsoft.svg" alt="" width={18} height={18} />
                        Continue with Microsoft
                      </a>
                    ) : null}
                  </div>
                  <div className="door-or">
                    <span>or</span>
                  </div>
                </>
              ) : null}

              <form className="auth-form" onSubmit={(event) => void submit(event)} noValidate>
                <label className="field">
                  Work email
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    placeholder="you@company.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </label>
                {siteKey ? (
                  <Turnstile ref={check} siteKey={siteKey} action="signin" onToken={setPassToken} />
                ) : null}
                <button
                  className={anyProvider ? "button button-quiet button-block" : "button button-block"}
                  type="submit"
                  disabled={busy || email.trim().length === 0}
                >
                  <Icon name="mail" size={18} />
                  {busy ? "Sending…" : "Email me a link"}
                </button>
              </form>

              {siteKey ? (
                <p className="door-fine">
                  {signingUp ? "Use your work address. " : "New to utuwrk? Sign in the same way. "}
                  Next you name your organisation, and it starts on Free.
                </p>
              ) : null}
              <p className="door-fine">
                {anyProvider
                  ? "We email you a link, or ask Google or Microsoft for your name and email address. Nothing is ever posted for you."
                  : "We email you a link. No password to remember."}
              </p>
            </>
          )}
        </section>
      </main>
    </div>
  );
}

function Feature({ icon, title, children }: { icon: IconName; title: string; children: string }) {
  return (
    <li className="door-feature">
      <span className="door-feature-icon">
        <Icon name={icon} size={20} />
      </span>
      <span>
        <strong>{title}</strong>
        <span>{children}</span>
      </span>
    </li>
  );
}

/** A refusal, in the worker's own words, with a word beside the colour (PRD 10.7). */
function Problem({ children }: { children: ReactNode }) {
  return (
    <p className="problem" role="alert">
      {children}
    </p>
  );
}

/* ----------------------------------------------------- the handoff demo -- */

interface Handoff {
  /** The card that finishes, and the department that owns it. */
  done: string;
  from: string;
  /** The card that opens because of it, and who is told. */
  next: string;
  to: string;
  who: string;
}

const DEMO: Handoff[] = [
  {
    done: "Laptop imaged",
    from: "IT",
    next: "Desk and badge ready",
    to: "Facilities",
    who: "New hire · starts Monday",
  },
  {
    done: "Offer letter signed",
    from: "HR",
    next: "Accounts created",
    to: "IT",
    who: "New hire · starts in two weeks",
  },
  {
    done: "Access removed",
    from: "IT",
    next: "Final pay approved",
    to: "Payroll",
    who: "Leaver · last day Friday",
  },
];

/** How long each step of the loop holds, in milliseconds: open, ticked, told. */
const BEATS = [1200, 900, 2800];

/**
 * A card finishing and the next owner being told: the product, in one loop.
 * Decoration only (`aria-hidden`), since the features beside it say the same
 * in words. With reduced motion it holds still on the first handoff, told.
 */
function HandoffDemo() {
  const still =
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const [step, setStep] = useState(still ? 2 : 0);
  const [which, setWhich] = useState(0);
  const handoff = DEMO[which % DEMO.length]!;

  useEffect(() => {
    if (still) return;
    const timer = window.setTimeout(() => {
      if (step < 2) setStep(step + 1);
      else {
        setStep(0);
        setWhich((current) => current + 1);
      }
    }, BEATS[step]);
    return () => window.clearTimeout(timer);
  }, [still, step]);

  const finished = step >= 1;
  return (
    <div className="handoff" aria-hidden="true" key={which}>
      <div className="handoff-card">
        <span className="handoff-check" data-done={finished || undefined}>
          {finished ? <Icon name="check" size={16} /> : null}
        </span>
        <span className="handoff-text">
          <strong>{handoff.done}</strong>
          <span>{handoff.who}</span>
        </span>
        <span className={finished ? "pill s-sent" : "pill s-waiting"}>
          {finished ? "Done" : handoff.from}
        </span>
      </div>
      <div className="handoff-notice" data-in={step === 2 || undefined}>
        <span className="handoff-bell">
          <Icon name="bell" size={18} />
        </span>
        <span className="handoff-text">
          <strong>Your turn, {handoff.to}</strong>
          <span>{handoff.next}</span>
        </span>
      </div>
    </div>
  );
}
